import * as Api from '@ViewModels';
import moment from 'moment';
import { useCallback, useMemo, useState } from 'react';

const findMatchingDateFilter = (
	searchProperty: Api.ContactFilterCriteriaProperty,
	criterion: Api.IContactFilterCriteria
) => {
	return criterion.criteria?.find(subCriterion => {
		return subCriterion.property === searchProperty;
	});
};

const getInitialValues = (
	searchProperty: Api.ContactFilterCriteriaProperty,
	criteria: Api.IContactFilterCriteria[]
) => {
	const dateCriteria = criteria?.find(criterion => findMatchingDateFilter(searchProperty, criterion));

	const relativeDate = dateCriteria?.criteria.find(criterion => {
		return criterion.values || criterion.value === 'never';
	});

	const startDateCriteria = dateCriteria?.criteria.find(criterion => {
		return criterion.op === Api.FilterOperator.Gte;
	});
	const startDate = !relativeDate && startDateCriteria?.value ? moment(startDateCriteria.value).toDate() : null;

	const endDateCriteria =
		!relativeDate &&
		dateCriteria?.criteria.find(criterion => {
			return criterion.op === Api.FilterOperator.Lte;
		});
	const endDate = endDateCriteria?.value ? moment(endDateCriteria.value).toDate() : null;
	return {
		startDate,
		endDate,
		relativeDate,
	};
};

export const useDateRangeFilter = (
	searchProperty: Api.ContactFilterCriteriaProperty,
	initialFilterCriteria: Api.IContactFilterCriteria[] = []
) => {
	const [startDate, setStartDate] = useState<Date | null>(
		getInitialValues(searchProperty, initialFilterCriteria)?.startDate
	);
	const [endDate, setEndDate] = useState<Date | null>(getInitialValues(searchProperty, initialFilterCriteria)?.endDate);
	const [relativeDate, setRelativeDate] = useState<Api.IContactFilterCriteria | null>(
		getInitialValues(searchProperty, initialFilterCriteria)?.relativeDate
	);

	const startDateCriteria: Api.IContactFilterCriteria | null = useMemo(() => {
		if (!startDate) {
			return null;
		}

		return {
			op: Api.FilterOperator.Gte,
			property: searchProperty,
			value: moment(startDate).format('YYYY-MM-DD'),
		};
	}, [startDate, searchProperty]);

	const endDateCriteria: Api.IContactFilterCriteria | null = useMemo(() => {
		if (!endDate) {
			return null;
		}
		return {
			op: Api.FilterOperator.Lte,
			property: searchProperty,
			value: moment(endDate).format('YYYY-MM-DD'),
		};
	}, [endDate, searchProperty]);

	const relativeDateCriteria: Api.IContactFilterCriteria = useMemo(() => {
		return relativeDate;
	}, [relativeDate]);

	const dateCriteria = useMemo(() => {
		const criteria = [];
		if (startDateCriteria) {
			criteria.push(startDateCriteria);
		}

		if (endDateCriteria) {
			criteria.push(endDateCriteria);
		}

		if (relativeDateCriteria) {
			criteria.push(relativeDateCriteria);
		}

		return {
			criteria,
			op: Api.FilterOperator.And,
		} as Api.IContactFilterCriteria;
	}, [startDateCriteria, endDateCriteria, relativeDateCriteria]);

	function getDateCriteria() {
		const newDateCriteria: PartiallyRequired<Api.IContactFilterCriteria, 'criteria'> = JSON.parse(
			JSON.stringify(dateCriteria)
		);
		return newDateCriteria;
	}

	const cancelDateChanges = useCallback(() => {
		setStartDate(null);
		setEndDate(null);
	}, []);

	const refreshDateFilter = (newKeyFactCriteria: Api.IContactFilterCriteria[]) => {
		const initialValue = getInitialValues(searchProperty, newKeyFactCriteria);
		setStartDate(initialValue.startDate);
		setEndDate(initialValue.endDate);
		setRelativeDate(initialValue.relativeDate);
	};

	return {
		cancelDateChanges,
		relativeDate,
		startDate,
		endDate,
		findMatchingDateFilter,
		setStartDate,
		setEndDate,
		setRelativeDate,
		getDateCriteria,
		refreshDateFilter,
	};
};
